/**
 * authService.js
 * All communication with the Node RBAC backend.
 */

import { RBAC_ENDPOINTS } from '../config/api';
import {
  storeAuthData,
  clearAuthData,
  getRefreshToken,
} from '../utils/auth';
import { rbacFetch } from '../utils/apiClient';

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function parseResponse(res) {
  const json = await res.json();
  if (!res.ok) {
    throw new Error(json.message || 'Request failed');
  }
  return json.data;
}

// ─── Auth API calls ───────────────────────────────────────────────────────────

/**
 * Login — stores tokens on success, returns user object
 */
export async function login(email, password) {
  const res = await fetch(RBAC_ENDPOINTS.login, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, password }),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json.message || 'Invalid email or password');
  }

  const { accessToken, refreshToken, user } = json.data;
  storeAuthData({ accessToken, refreshToken, user });
  return user;
}

/**
 * Logout — clears tokens locally and revokes on server
 */
export async function logout() {
  try {
    await rbacFetch(RBAC_ENDPOINTS.logout, { method: 'POST' });
  } catch {
    // Even if server call fails, clear local tokens
  } finally {
    clearAuthData();
  }
}

/**
 * Refresh access token using stored refresh token
 * Returns new user object or null on failure
 */
export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await fetch(RBAC_ENDPOINTS.refreshToken, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ refreshToken }),
    });

    const json = await res.json();
    if (!res.ok) {
      clearAuthData();
      return null;
    }

    const { accessToken, refreshToken: newRefreshToken, user } = json.data;
    storeAuthData({ accessToken, refreshToken: newRefreshToken, user });
    return user;
  } catch {
    clearAuthData();
    return null;
  }
}

/**
 * Get current user profile from server
 */
export async function getMe() {
  const res = await rbacFetch(RBAC_ENDPOINTS.me);
  return parseResponse(res);
}

// ─── User Management API calls (superadmin only) ──────────────────────────────

export async function fetchUsers({ page = 1, limit = 50, search = '', role = '' } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (search) params.set('search', search);
  if (role)   params.set('role', role);

  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}?${params}`);
  return parseResponse(res);
}

export async function fetchUserById(id) {
  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}/${id}`);
  return parseResponse(res);
}

export async function createUser(payload) {
  const res = await rbacFetch(RBAC_ENDPOINTS.users, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function updateUser(id, payload) {
  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}/${id}`, {
    method: 'PUT',
    body:   JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function deleteUser(id) {
  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}/${id}`, {
    method: 'DELETE',
  });
  return parseResponse(res);
}

export async function fetchRoles() {
  const res = await rbacFetch(RBAC_ENDPOINTS.roles);
  return parseResponse(res);
}

export async function fetchFilterConfig() {
  const res = await rbacFetch(RBAC_ENDPOINTS.filtersConfig);
  return parseResponse(res);
}

export async function fetchAuditLogs({ page = 1, limit = 50, action = '', user_id = '', date_from = '', date_to = '' } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (action)    params.set('action',    action);
  if (user_id)   params.set('user_id',   user_id);
  if (date_from) params.set('date_from', date_from);
  if (date_to)   params.set('date_to',   date_to);
  const res = await rbacFetch(`${RBAC_ENDPOINTS.auditLogs}?${params}`);
  return parseResponse(res);
}

export async function fetchSyncPreview() {
  const res = await rbacFetch(RBAC_ENDPOINTS.syncPreview);
  return parseResponse(res);
}

export async function importSyncDoctors(doctors) {
  const res = await rbacFetch(RBAC_ENDPOINTS.syncImport, {
    method: 'POST',
    body:   JSON.stringify({ doctors }),
  });
  return parseResponse(res);
}

export async function forgotPassword(email) {
  const res = await fetch(RBAC_ENDPOINTS.forgotPassword, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email }),
  });
  const json = await res.json();
  return json.data;
}

export async function resetPassword(token, password) {
  const res = await fetch(RBAC_ENDPOINTS.resetPassword, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ token, password }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Reset failed');
  return json.data;
}

export async function changePassword(currentPassword, newPassword) {
  const res = await rbacFetch(RBAC_ENDPOINTS.changePassword, {
    method: 'POST',
    body:   JSON.stringify({ currentPassword, newPassword }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to change password');
  return json.data;
}

/**
 * Accept Terms & Conditions — called once on first login
 * Updates terms_accepted in DB and returns success
 */
export async function acceptTerms() {
  const res = await rbacFetch(RBAC_ENDPOINTS.acceptTerms, {
    method: 'POST',
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Failed to accept terms');
  return json.data;
}

// ─── Assignment APIs ──────────────────────────────────────────────────────────

export async function fetchUserAssignments(userId) {
  const res = await rbacFetch(RBAC_ENDPOINTS.userAssignments(userId));
  return parseResponse(res);
}

export async function fetchUserHospitals(userId) {
  const res = await rbacFetch(RBAC_ENDPOINTS.userHospitals(userId));
  return parseResponse(res);
}

export async function updateUserAssignments(userId, payload) {
  const res = await rbacFetch(RBAC_ENDPOINTS.userAssignments(userId), {
    method: 'PUT',
    body:   JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function fetchMetaHospitals() {
  const res = await rbacFetch(RBAC_ENDPOINTS.metaHospitals);
  return parseResponse(res);
}

export async function fetchMetaZones() {
  const res = await rbacFetch(RBAC_ENDPOINTS.metaZones);
  return parseResponse(res);
}

export async function fetchMetaSalesReps() {
  const res = await rbacFetch(RBAC_ENDPOINTS.metaSalesReps);
  return parseResponse(res);
}

export async function fetchMetaZBMs() {
  const res = await rbacFetch(RBAC_ENDPOINTS.metaZBMs);
  return parseResponse(res);
}
